//Built-in libraries
import Card from '@/components/UI/Card';
import WidgetLayout from '@/components/Widgets/WidgetLayout';
import type { ColumnDef } from '@tanstack/react-table';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import { usePermission } from '@/hooks/usePermission';
import { notifyUser } from '@/store/Toast';
import { convertEnumToText } from '@/utils';
//Services
import { useDashboardData } from '@/hooks/ReactQueryHooks/useDashboardQuery';
//Assets and Miscellaneous
// import Styles from "./Status.module.css";
import type { DashboardType } from '@/types/Analytics.ts';
import { CurrencyFormatDisplay } from '../../UI/NumberFormatDisplay/CurrencyFormatDisplay';
//Custom components
import Table from '../../UI/Table';
import { Widget } from '../constants';

interface ProjectSummaryWidgetProps {
  dashboard: DashboardType;
}

const StatusWidget = ({ dashboard }: ProjectSummaryWidgetProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { hasPermission } = usePermission();

  const [perPage, setPerPage] = useState<number>(10);
  const [page, setPage] = useState<number>(1);
  const [totalPage, _setTotalPage] = useState<number>(1);

  const nextPageHandler = () => {
    setPage((prevState) => (prevState === totalPage ? totalPage : page + 1));
  };

  const previousPageHandler = () => {
    setPage((prevState) => (prevState > 1 ? page - 1 : 1));
  };

  const projectDetailViewHandler = (projectId: number) => {
    if (hasPermission(['READ_PROJECT'])) {
      navigate(`${location.pathname.replace('dashboard', 'projects')}/${projectId}/dashboard`);
    } else {
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: 'You do not have permission to read project details',
          toastType: 'info',
        })
      );
    }
  };

  const fetchProjectSummaryWidgetData = useDashboardData({
    dashboard,
    widget: Widget.PROJECTS_SUMMARY_REPORT,
  });

  const projectSummaryWidgetData = fetchProjectSummaryWidgetData?.data?.data;
  const tableColumns: ColumnDef<any>[] = [
    {
      id: 'name',
      header: () => <div className='text-start'>Project Name</div>,
      accessorKey: 'name',
      cell: (props) => {
        const { getValue, row } = props;
        const { id } = row.original || {};
        return (
          <div className='text-start hover:underline' onClick={() => projectDetailViewHandler(id)}>
            <span>{getValue() as string}</span>
          </div>
        );
      },
    },
    {
      id: 'district',
      header: () => <div className='text-start'>District</div>,
      accessorKey: 'district',
      cell: (props) => {
        const { getValue } = props;
        return (
          <div className='text-start'>
            <span>{getValue() as string}</span>
          </div>
        );
      },
    },
    {
      id: 'totalDevelopmentCost',
      header: () => <div className='text-end'>Total Development Value</div>,
      accessorKey: 'totalDevelopmentCost',
      cell: (props) => {
        const { getValue } = props;
        return (
          <div className='text-end'>
            <CurrencyFormatDisplay value={getValue() as number} prefix='$' />
          </div>
        );
      },
    },
    {
      id: 'cityContribution',
      header: () => <div className='text-end'>City Contribution</div>,
      accessorKey: 'cityContribution',
      cell: (props) => {
        const { getValue } = props;
        return (
          <div className='text-end'>
            <CurrencyFormatDisplay value={getValue() as number} prefix='$' />
          </div>
        );
      },
    },
    // {
    //   id: 'six',
    //   header: () => <div className='text-center'># of Units</div>,
    //   accessorKey: 'six',
    //   cell: (props) => {
    //     const { getValue } = props;
    //     return (
    //       <div className='text-center'>
    //         <CurrencyFormatDisplay value={getValue() as number} />
    //       </div>
    //     );
    //   },
    // },
    {
      id: 'status',
      header: () => <div className='text-start pl-10'>Status</div>,
      accessorKey: 'status',
      cell: (props) => {
        const { getValue } = props;
        return (
          <div className='text-start pl-10'>
            <span className=''> {convertEnumToText(getValue() as string)}</span>
          </div>
        );
      },
    },
  ];

  const tablePagination = {
    page: page,
    pageSize: perPage,
    pageCount: totalPage,
    currentPage: page,
    setPageSize: (v: number) => setPerPage(v),
    nextPage: nextPageHandler,
    previousPage: previousPageHandler,
  };

  return (
    <WidgetLayout dashboard={dashboard} widget={Widget.PROJECT_STATUS_SUMMARY}>
      <Card title='Project Summary'>
        <div className='h-full w-full'>
          <div className='flex flex-row  bg-white justify-start items-center rounded-t-md'>
            <div className='pr-2 text-neutralColor-light cursor-pointer hover:text-neutralColor-medium transition duration-300 ease-in-out'></div>
          </div>
          {projectSummaryWidgetData && (
            <div className='h-96 border rounded-b-md w-full bg-white'>
              <Table cols={tableColumns} tableData={projectSummaryWidgetData} pagination={tablePagination} height='h-5/6' />
            </div>
          )}
        </div>
      </Card>
    </WidgetLayout>
  );
};

export default StatusWidget;
